
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { NodeData, NodeType } from '../../types';
import { Upload, X, Plus, Sparkles, Loader2, Cpu, Download, Play, Link as LinkIcon } from 'lucide-react';
import { nodeKind } from './nodeTheme';
import { SettingChip } from './composer/Composer';
import { PROVIDER_NAMES, shortModelLabel } from './composer/modelLabels';
import { GoogleGenAI } from "@google/genai";
import { loadLocalModel, generateLocal, isLocalModelLoaded } from '../../utils/webLlmService';
import { getTextModels, AI_MODELS } from '../../models/config';
import type { InitProgressReport } from '@mlc-ai/web-llm';
import { useFileUrls } from '../../hooks/useFileUrl';
import { FileImage } from '../FileImage';
import { loadMediaAsBase64 } from '../../utils/mediaLoader';

interface EntityNodeProps {
  data: NodeData;
  type: NodeType;
  onDataChange: (newData: NodeData) => void;
  apiKey?: string;
  inputImages?: { id: string, url: string, label?: string }[];
  userId?: string; // For resolving file:// references
  workspaceName?: string; // For local-first file resolution
  isViewOnly?: boolean;
  ownerUserId?: string;
}

export const EntityNode: React.FC<EntityNodeProps> = ({ data, type, onDataChange, apiKey, inputImages = [], userId, workspaceName, isViewOnly = false, ownerUserId }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');
  const [localModelReady, setLocalModelReady] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  
  // Model selection state
  // Defaults to Gemini Flash if not set
  const selectedModelId = data.entityData?.model || 'gemini-2.5-flash';
  const textModels = useMemo(() => getTextModels(), []);
  // The model chip's list: the cloud's models under their provider, those that run in the browser under "On this computer"
  const modelOptions = useMemo(
      () => textModels.map(m => ({ value: m.id, label: shortModelLabel(m.label), group: m.provider === 'webllm' ? 'On this computer' : PROVIDER_NAMES[m.provider] ?? m.provider })),
      [textModels],
  );
  
  const modelDef = AI_MODELS[selectedModelId];
  const isLocal = modelDef?.provider === 'webllm';
  
  const entityData = data.entityData || { name: 'Untitled', description: '' };
  const rawUploadedRefs = data.referenceImages || (data.imageUrl ? [data.imageUrl] : []);
  
  // Resolve file:// references to actual URLs for uploaded images (local-first if auto-save enabled)
  const { urls: resolvedUploadedUrls } = useFileUrls(userId, rawUploadedRefs, workspaceName, ownerUserId);
  const uploadedRefs = resolvedUploadedUrls.map((url, i) => url || rawUploadedRefs[i]) as string[];
  
  const connectedRefs = inputImages.map(img => img.url);
  // The stored references, not the addresses they are shown from: the loader reads a reference
  // from the local copy first and from the cloud when that file has gone
  const allReferences = [...rawUploadedRefs, ...connectedRefs];
  
  // The icon is the kind's own (nodeTheme.tsx), the same as in the node's header. The colour is
  // the node's tone (coral, teal or sage), which the card sets for everything inside it
  const kind = nodeKind(type);
  const Icon = kind.icon;
  let entityLabel = 'Character';

  if (type === NodeType.PROP_NODE) {
      entityLabel = 'Prop';
  } else if (type === NodeType.LOCATION_NODE) {
      entityLabel = 'Location/Set';
  }

  // Check Local Model Status
  useEffect(() => {
      if (isLocal) {
          const checkReady = () => setLocalModelReady(isLocalModelLoaded(selectedModelId));
          checkReady();
          const interval = setInterval(checkReady, 1000);
          return () => clearInterval(interval);
      } else {
          setLocalModelReady(true);
      }
  }, [selectedModelId, isLocal]);

  // --- Helpers ---
  /** Bytes of a reference image for the provider request; errors name the input. */
  const getBase64FromUrl = (url: string, label = 'a reference image') =>
      loadMediaAsBase64(url, { userId, ownerUserId, workspaceName, label, defaultMimeType: 'image/png' });

  const updateModel = (modelId: string) => {
      onDataChange({
          ...data,
          entityData: {
              ...entityData,
              model: modelId
          }
      });
  };

  const handleUpdate = (field: string, value: string) => {
      if (isViewOnly) return;
      onDataChange({
          ...data,
          entityData: {
              ...entityData,
              [field]: value
          }
      });
  };

  const handleLoadModel = async () => {
      if (isViewOnly) return;
      setIsGenerating(true);
      setDownloadProgress('Initializing...');
      try {
          await loadLocalModel(selectedModelId, (report: InitProgressReport) => {
              setDownloadProgress(report.text);
          });
          setLocalModelReady(true);
      } catch (e: any) {
          alert("Load Failed: " + e.message);
      } finally {
          setIsGenerating(false);
          setDownloadProgress('');
      }
  };

  // --- Auto Generate Description ---
  const handleAutoDescription = async () => {
      if (isViewOnly) return;
      // Validation
      if (!isLocal) {
          const keyToUse = apiKey || process.env.API_KEY;
          if (!keyToUse) {
              alert("API Key required for cloud models. Please set it in Settings.");
              return;
          }
      }
      
      if (allReferences.length === 0 && !entityData.description && !isLocal) {
          alert("Please upload at least one reference image or provide a basic description to enhance.");
          return;
      }

      setIsGenerating(true);
      setDownloadProgress('');

      try {
          // --- LOCAL GENERATION ---
          if (isLocal) {
               // Double check loaded
               if (!isLocalModelLoaded(selectedModelId)) {
                   await handleLoadModel();
                   if (!isLocalModelLoaded(selectedModelId)) return; // failed to load
               }
               
               setDownloadProgress('Generating...');

               // Note: Local text models cannot see images yet in this implementation
               // We only use the text name and existing description
               const prompt = `
                 You are a visual design expert. 
                 Write a detailed visual description for a ${entityLabel} named "${entityData.name}".
                 ${entityData.description ? `Enhance this draft: "${entityData.description}"` : ''}
                 
                 Focus on: appearance, colors, materials, style, mood.
                 Keep it under 100 words.
               `;
               
               const response = await generateLocal(prompt, "You are a creative assistant.");
               // Strip thinking tags if present (DeepSeek)
               const cleanResponse = response.replace(/<think>[\s\S]*?<\/think>/g, '').trim();
               handleUpdate('description', cleanResponse);

          } else {
              // --- CLOUD GENERATION ---
              const ai = new GoogleGenAI({ apiKey: apiKey || process.env.API_KEY });
              const parts: any[] = [];
              
              if (allReferences.length > 0) {
                  const processedImages = await Promise.all(allReferences.map((url, i) => getBase64FromUrl(url, `Reference ${i + 1}`)));
                  for (const { data, mimeType } of processedImages) {
                      parts.push({ inlineData: { mimeType, data } });
                  }
              }

              const basePrompt = `
                You are a visual design expert for film and media. 
                Analyze the provided reference images (if any) and the current name: "${entityData.name}".
                
                Task: Write a detailed, vivid visual description of this ${entityLabel} that can be used as a prompt for an AI Image Generator.
                
                Focus on:
                - Physical appearance (colors, textures, materials)
                - Style/Mood
                - Key distinctive features
                - Lighting/Environment context (if relevant to the set/location)
                
                Keep it concise but descriptive (under 100 words).
              `;
              
              parts.push({ text: basePrompt });
              if (entityData.description) {
                  parts.push({ text: `Current draft description to enhance: "${entityData.description}"` });
              }

              const response = await ai.models.generateContent({
                  model: 'gemini-2.5-flash', // We force multimodal capable model for image analysis
                  contents: { parts }
              });
              
              const generatedText = response.text;
              if (generatedText) {
                  handleUpdate('description', generatedText);
              }
          }

      } catch (e: any) {
          console.error("Auto-describe failed", e);
          alert("Failed: " + e.message);
      } finally {
          setIsGenerating(false);
          setDownloadProgress('');
      }
  };


  // --- Image Upload Logic ---
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isViewOnly) return;
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const promises = Array.from(files).map(file => {
          return new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = (ev) => resolve(ev.target?.result as string);
              reader.readAsDataURL(file);
          });
      });

      const results = await Promise.all(promises);
      // What is kept is each picture's stored reference, never the address it is shown from (that only lives for this session)
      const currentRefs = rawUploadedRefs;
      const updatedRefs = [...currentRefs, ...results];

      onDataChange({
          ...data,
          imageUrl: updatedRefs[0], 
          referenceImages: updatedRefs
      });
      
      if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = (index: number) => {
      if (isViewOnly) return;
      const updatedRefs = [...rawUploadedRefs];
      updatedRefs.splice(index, 1);
      onDataChange({
          ...data,
          imageUrl: updatedRefs.length > 0 ? updatedRefs[0] : undefined,
          referenceImages: updatedRefs
      });
  };

  // Drag and drop handlers for images from Storage node
  const handleDragOver = (e: React.DragEvent) => {
      if (isViewOnly) return;
      e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
      if (isViewOnly) return;
      e.preventDefault();
      try {
          const jsonData = e.dataTransfer.getData('application/json');
          if (jsonData) {
              const parsed = JSON.parse(jsonData);
              if (parsed.type === 'storage-file' && parsed.file?.url) {
                  // Add the dropped image URL to reference images
                  const currentRefs = rawUploadedRefs;
                  const updatedRefs = [...currentRefs, parsed.file.url];
                  onDataChange({
                      ...data,
                      imageUrl: updatedRefs[0],
                      referenceImages: updatedRefs
                  });
                  return;
              }
          }
      } catch (err) {
          // Not JSON data, try file drop
      }

      // Handle regular file drop
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (ev) => {
              const result = ev.target?.result as string;
              const currentRefs = rawUploadedRefs;
              const updatedRefs = [...currentRefs, result];
              onDataChange({
                  ...data,
                  imageUrl: updatedRefs[0],
                  referenceImages: updatedRefs
              });
          };
          reader.readAsDataURL(file);
      }
  };

  // The card's header says what kind of node this is; the row under it holds the name, where it
  // is edited. The description takes the height the card has to spare (a field in a card has no
  // grip of its own: the card's corner sizes it). Under it: the model, as a chip that opens a
  // list like the chips of the Image and Video nodes, and the one action, in ink.
  return (
    <div className="w-full h-full flex flex-col bg-white">
        {/* Name */}
        <div className="shrink-0 px-3 py-2 border-b border-cream-200 flex items-center gap-2 bg-cream-50">
            <span className="ns-mark ns-mark-sm">
                <Icon />
            </span>
            <input
                className="ns-t-title bg-transparent focus:outline-none w-full min-w-0 placeholder:text-ink-faint placeholder:font-normal"
                value={entityData.name}
                onChange={(e) => handleUpdate('name', e.target.value)}
                placeholder={`${kind.label} name`}
                aria-label={`${kind.label} name`}
                title={entityData.name}
            />
        </div>

        {type === NodeType.LOCATION_NODE && (
            <div className="shrink-0 px-3 pt-3 flex">
                <div className="ns-seg flex w-full">
                    <button
                        className="ns-seg-item flex-1"
                        aria-pressed={entityData.setType === 'Interior'}
                        onClick={() => handleUpdate('setType', 'Interior')}
                        title="Interior"
                    >
                        INT.
                    </button>
                    <button
                        className="ns-seg-item flex-1"
                        aria-pressed={entityData.setType === 'Exterior'}
                        onClick={() => handleUpdate('setType', 'Exterior')}
                        title="Exterior"
                    >
                        EXT.
                    </button>
                </div>
            </div>
        )}

        {/* Description */}
        <div className="relative flex-1 flex flex-col p-3 border-b border-cream-200 bg-white">
            {/* While a model loads or writes: what it is doing, over the field */}
            {isGenerating && downloadProgress && (
                <div className="absolute inset-0 bg-white/95 z-10 flex flex-col items-center justify-center text-center p-2">
                    <span className="ns-t-meta text-ink-soft">{downloadProgress}</span>
                    <div className="ns-meter max-w-[100px] mt-1.5" data-tone={kind.tone}>
                        <span className="w-1/2 animate-pulse" />
                    </div>
                </div>
            )}

            <textarea
                className="ns-field block flex-1 min-h-[58px]"
                placeholder="Visual description..."
                aria-label="Description"
                value={entityData.description}
                onChange={(e) => handleUpdate('description', e.target.value)}
            />

            {/* The model that writes the description, and the button that has it written */}
            <div className="shrink-0 flex items-center justify-between gap-2 mt-2">
                <SettingChip
                    icon={isLocal ? <Cpu size={14} /> : <Sparkles size={14} />}
                    label="Model"
                    value={selectedModelId}
                    options={modelOptions}
                    onChange={updateModel}
                    disabled={isViewOnly}
                    shrink
                />

                {isLocal && !localModelReady ? (
                    <button
                       onClick={handleLoadModel}
                       disabled={isGenerating}
                       className="ns-btn ns-btn-primary ns-btn-xs shrink-0"
                       title="Load the model first"
                    >
                       {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                       Load model
                    </button>
                ) : (
                    <button
                       onClick={handleAutoDescription}
                       disabled={isGenerating}
                       className="ns-btn ns-btn-primary ns-btn-xs shrink-0"
                       title="Auto-describe"
                    >
                        {/* The same word and mark as the Storyboard's "Generate all" */}
                        {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Play size={12} fill="currentColor" />}
                        {isGenerating ? 'Generating' : 'Generate'}
                    </button>
                )}
            </div>
        </div>

        {/* Reference pictures */}
        <div
            className={`shrink-0 p-3 flex flex-col transition-colors ${
                isDraggingOver ? 'bg-[color:var(--tone-soft)]' : 'bg-cream-50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div className="flex items-center justify-between gap-2 mb-1.5 min-h-[24px]">
                <span className="ns-t-label">Visual References ({allReferences.length})</span>
                {allReferences.length > 0 && (
                     <button
                        onClick={() => fileInputRef.current?.click()}
                        className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-line"
                        title="Add reference"
                        aria-label="Add reference"
                     >
                         <Plus />
                     </button>
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
            />

            {allReferences.length > 0 ? (
                <div className={`grid grid-cols-3 gap-2 overflow-y-auto max-h-[160px] pr-1 ${isDraggingOver ? 'pointer-events-none' : ''}`}>
                    {/* Connected pictures: what is linked from another node is ringed and marked in the node's own colour */}
                    {inputImages.map((img, idx) => (
                        <div key={`conn-${idx}`} className="relative aspect-square rounded-lg overflow-hidden border-2 border-[color:var(--tone)] bg-cream-200 group" title={`Connected: ${img.label || 'Image'}`}>
                            <FileImage src={img.url} userId={userId} workspaceName={workspaceName} className="w-full h-full object-cover" alt={`Connected Ref ${idx}`} />
                            <div className="absolute top-1 right-1 w-5 h-5 grid place-items-center bg-[color:var(--tone-deep)] text-white rounded-full shadow-hair">
                                <LinkIcon size={12} />
                            </div>
                        </div>
                    ))}

                    {/* Uploaded pictures */}
                    {uploadedRefs.map((ref, idx) => (
                        <div key={`up-${idx}`} className="relative aspect-square rounded-lg overflow-hidden border border-cream-300 bg-cream-200 group">
                            <FileImage src={ref} userId={userId} workspaceName={workspaceName} className="w-full h-full object-cover" alt={`Ref ${idx}`} />
                            <button
                                onClick={() => handleRemoveImage(idx)}
                                className="node-reveal ns-icon-btn ns-icon-btn-xs ns-icon-btn-dark absolute top-1 right-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                                title="Remove reference"
                                aria-label="Remove reference"
                            >
                                <X />
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                // Nothing yet: the one pattern for it (a round mark and a line of text), small enough for this strip
                <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`ns-empty gap-0 px-3 py-1.5 min-h-[72px] rounded-lg cursor-pointer transition-colors ${
                        isDraggingOver ? '' : 'hover:bg-cream-200'
                    }`}
                >
                    <span className="ns-mark ns-mark-sm">
                        <Upload />
                    </span>
                    <span className="ns-empty-text">
                        {isDraggingOver ? 'Drop image here' : 'Upload or drag images'}
                    </span>
                </div>
            )}
        </div>
    </div>
  );
};
